/**
 * Email Lookup Service
 * Handles checking if emails exist in customers/users and extracting customer data from notes
 */

/**
 * Check if email exists in customers or users
 * @param {string} email - Email address to check
 * @param {Function} getProtectedTenantCode - Function to get tenant code
 * @returns {Promise<{found: boolean, type: 'customer'|'user'|null, data: object|null}>}
 */
export const checkEmailExists = async (email, getProtectedTenantCode) => {
  if (!email) {
    return { found: false, type: null, data: null };
  }

  try {
    const tenantCode = getProtectedTenantCode();
    const selectedRole = localStorage.getItem('selectedRole');
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${localStorage.getItem('token')}`,
      ...(tenantCode ? { 'X-Tenant-Code': tenantCode } : {}),
      ...(selectedRole ? { 'X-Selected-Role': selectedRole } : {})
    };

    // First check customers
    const customerResponse = await fetch('/api/system/find-customer-by-email', {
      method: 'POST',
      headers,
      body: JSON.stringify({ email })
    });

    if (customerResponse.ok) {
      const customerData = await customerResponse.json();
      if (customerData.customer) {
        return {
          found: true,
          type: 'customer',
          data: customerData.customer
        };
      }
    }

    // If not found in customers, check users
    const userResponse = await fetch('/api/system/find-user-by-email', {
      method: 'POST',
      headers,
      body: JSON.stringify({ email })
    });

    if (userResponse.ok) {
      const userData = await userResponse.json();
      if (userData.user) {
        return {
          found: true,
          type: 'user',
          data: userData.user
        };
      }
    }

    return { found: false, type: null, data: null };
  } catch (error) {
    console.error('Error checking email existence:', error);
    return { found: false, type: null, data: null, error: error.message };
  }
};

/**
 * Extract customer data from note text using AI
 * @param {string} noteText - The note text to analyze
 * @param {string} email - The email address to match against extracted data
 * @param {Function} getProtectedTenantCode - Function to get tenant code
 * @returns {Promise<{success: boolean, data: object|null, error: string|null}>}
 */
export const extractCustomerDataFromNote = async (noteText, email, getProtectedTenantCode) => {
  if (!noteText || !email) {
    return { success: false, data: null, error: 'Note text and email are required' };
  }

  try {
    const tenantCode = getProtectedTenantCode();
    const selectedRole = localStorage.getItem('selectedRole');
    const headers = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${localStorage.getItem('token')}`,
      ...(tenantCode ? { 'X-Tenant-Code': tenantCode } : {}),
      ...(selectedRole ? { 'X-Selected-Role': selectedRole } : {})
    };

    // Create a focused prompt that includes the email for better extraction
    // Extract context around the email in the note
    const emailIndex = noteText.toLowerCase().indexOf(email.toLowerCase());
    let contextText = noteText;
    
    if (emailIndex !== -1) {
      // Get 500 characters before and after the email for context
      const start = Math.max(0, emailIndex - 500);
      const end = Math.min(noteText.length, emailIndex + email.length + 500);
      contextText = noteText.substring(start, end);
    }

    // Call AI chat endpoint with a focused extraction prompt
    const extractionPrompt = `Extract customer information from this note for email: ${email}

Note text:
${noteText}

Please extract ALL available information about the person with email ${email}. Look for:
- First name and last name (both Thai and English if available)
- Phone number
- Company name
- Job title
- Department
- Any other contact details

Return ONLY a JSON object in this exact format (no markdown, no explanation):
{
  "firstName": "",
  "lastName": "",
  "firstName_eng": "",
  "lastName_eng": "",
  "email": "${email}",
  "phone": "",
  "companyName": "",
  "jobTitle": "",
  "department": ""
}

If you cannot find a field, leave it as an empty string. Do not add any text before or after the JSON.`;
    
    const response = await fetch('/api/ai/chat/chat', {
      method: 'POST',
      headers,
      body: JSON.stringify({ 
        message: extractionPrompt,
        history: [],
        options: {
          agentType: 'data_extraction',
          useSystemPrompt: false,
          temperature: 0.1,
          max_tokens: 500
        }
      })
    });

    if (!response.ok) {
      console.error('AI extraction failed, falling back to pattern matching');
      const fallbackData = extractBasicInfoFromText(noteText, email);
      return { success: true, data: fallbackData, error: null };
    }

    const result = await response.json();
    console.log('AI extraction response:', result);
    
    // Extract the AI's reply
    const aiReply = result.data?.reply || result.reply || result.response || '';
    
    if (!aiReply) {
      console.error('No AI reply found, falling back to pattern matching');
      const fallbackData = extractBasicInfoFromText(noteText, email);
      return { success: true, data: fallbackData, error: null };
    }

    // Try to parse JSON from the reply
    let extractedData = null;
    try {
      // Remove markdown code blocks if present
      let jsonText = aiReply.trim();
      if (jsonText.startsWith('```json')) {
        jsonText = jsonText.replace(/```json\n?/g, '').replace(/```\n?/g, '').trim();
      } else if (jsonText.startsWith('```')) {
        jsonText = jsonText.replace(/```\n?/g, '').trim();
      }
      
      extractedData = JSON.parse(jsonText);
      console.log('Successfully parsed AI extracted data:', extractedData);
      
      // Ensure email is always set
      extractedData.email = email;
      
      return { success: true, data: extractedData, error: null };
    } catch (parseError) {
      console.error('Failed to parse AI response as JSON:', parseError);
      console.log('AI response was:', aiReply);
      // Fall back to pattern matching
      const fallbackData = extractBasicInfoFromText(noteText, email);
      return { success: true, data: fallbackData, error: null };
    }

    // If no customer found, try to extract basic info from note text using regex patterns
    const fallbackData = extractBasicInfoFromText(noteText, email);
    
    return {
      success: true,
      data: fallbackData,
      error: null
    };
  } catch (error) {
    console.error('Error extracting customer data from note:', error);
    return {
      success: false,
      data: null,
      error: error.message || 'Failed to extract customer data'
    };
  }
};

/**
 * Extract basic information from text using pattern matching
 * This is a fallback when AI extraction doesn't find structured data
 * @param {string} text - The text to analyze
 * @param {string} email - The email address
 * @returns {object} Basic extracted data
 */
const extractBasicInfoFromText = (text, email) => {
  const data = {
    email: email,
    firstName: '',
    lastName: '',
    phone: '',
    title: '',
    jobTitle: '',
    department: '',
    companyName: ''
  };

  if (!text) return data;

  // Try to find name patterns near the email
  const emailIndex = text.toLowerCase().indexOf(email.toLowerCase());
  if (emailIndex !== -1) {
    // Get context around email (200 chars before and after)
    const start = Math.max(0, emailIndex - 200);
    const end = Math.min(text.length, emailIndex + email.length + 200);
    const context = text.substring(start, end);

    // Extract phone numbers (various formats)
    const phonePatterns = [
      /(\+?\d{1,3}[-.\s]?)?\(?\d{3}\)?[-.\s]?\d{3}[-.\s]?\d{4}/g,
      /(\+66|0)[\d\s-]{9,}/g, // Thai phone format
      /\d{3}[-.\s]?\d{3}[-.\s]?\d{4}/g
    ];
    
    for (const pattern of phonePatterns) {
      const matches = context.match(pattern);
      if (matches && matches.length > 0) {
        data.phone = matches[0].trim();
        break;
      }
    }

    // Try to extract name from lines before email
    const linesBeforeEmail = context.split('\n').slice(0, 5);
    for (const line of linesBeforeEmail) {
      // Look for name-like patterns (2-3 words, capitalized)
      const nameMatch = line.match(/\b([A-Z][a-z]+(?:\s+[A-Z][a-z]+){1,2})\b/);
      if (nameMatch && !nameMatch[1].includes('@') && nameMatch[1].length > 3) {
        const nameParts = nameMatch[1].trim().split(/\s+/);
        if (nameParts.length >= 2) {
          data.firstName = nameParts[0];
          data.lastName = nameParts.slice(1).join(' ');
        } else {
          data.firstName = nameParts[0];
        }
        break;
      }
    }

    // Try to extract company name
    const companyPatterns = [
      /(?:Company|บริษัท|Corp|Corporation|Ltd|Limited|Co\.|Company:)\s*:?\s*([A-Z][A-Za-z0-9\s&.,-]+)/i,
      /\b([A-Z][A-Za-z0-9\s&.,-]{3,})\s+(?:Company|Corp|Ltd|Inc)\b/i
    ];
    
    for (const pattern of companyPatterns) {
      const match = context.match(pattern);
      if (match && match[1]) {
        data.companyName = match[1].trim();
        break;
      }
    }
  }

  return data;
};

